import { useEffect, useState } from "react";
import { getApkManifest, formatBytes, type ApkManifest } from "../api/downloads";
import QrCode from "./QrCode";
import "./participate.css";

const STEPS = [
  { icon: "download", text: "Descarga la app CICLOPE desde este QR o el botón de arriba." },
  { icon: "install", text: "Instálala en tu Android — puede pedirte permitir instalación." },
  { icon: "bluetooth", text: "Activa Bluetooth y “Dispositivos cercanos”." },
  { icon: "tap", text: "Abre la app y toca PARTICIPAR." },
  { icon: "target", text: "Sigue la señal: FRÍO → ACERCÁNDOTE → CERCA → MUY CERCA." },
];

const RULES = [
  "Participación voluntaria.",
  "No requiere login.",
  "No requiere nombre, correo o teléfono.",
  "Las detecciones se manejan con un identificador anónimo.",
  "No correr.",
  "Respeta las áreas restringidas.",
  "No interfieras con otros stands.",
  "Sigue las instrucciones del Staff CICLOPE.",
  "El objetivo puede estar en movimiento.",
  "La proximidad BLE es aproximada, no exacta.",
];

const FAQ = [
  {
    q: "Android bloqueó la instalación, ¿ahora qué?",
    a: "Es normal la primera vez. Ve a Configuración → Apps → Acceso especial → Instalar apps desconocidas, elige tu navegador y activa “Permitir de esta fuente”. Luego vuelve a abrir la descarga.",
  },
  {
    q: "¿Cómo activo Bluetooth y dispositivos cercanos?",
    a: "Al abrir la app por primera vez te pedirá permiso de Bluetooth y de “Dispositivos cercanos” (Android 12+). Acepta ambos — sin ellos la app no puede detectar el Beacon.",
  },
  {
    q: "¿La app pide mis datos personales?",
    a: "No. No hay registro, login, ni se te pide nombre, correo o teléfono. Cada participante se identifica con un código anónimo generado en el dispositivo.",
  },
];

const APK_URL = "/downloads/CICLOPE_AIM_2026.apk";

const ICONS: Record<string, React.ReactElement> = {
  download: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 20h14" />
    </svg>
  ),
  install: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="2.5" width="12" height="19" rx="2.5" /><path d="M11 18.5h2" />
    </svg>
  ),
  bluetooth: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="m6.5 6.5 11 11L12 22V2l5.5 4.5-11 11" />
    </svg>
  ),
  tap: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 12.5V5a2 2 0 1 1 4 0v6" /><path d="M13 11V4a2 2 0 1 1 4 0v8" />
      <path d="M17 11.5a2 2 0 1 1 4 0V15c0 4-2.5 7-6.5 7h-1c-3 0-4.3-1-5.8-3l-3-4.7c-.6-1 -.3-2 .5-2.5.8-.5 1.9-.3 2.5.5L10 13" />
    </svg>
  ),
  target: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  ),
};

export default function Participate() {
  const [manifest, setManifest] = useState<ApkManifest | null>(null);
  const [apkAvailable, setApkAvailable] = useState<boolean | null>(null);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setShareUrl(`${location.origin}/participa`);
    void getApkManifest().then(setManifest);
    fetch(APK_URL, { method: "HEAD" })
      .then((r) => setApkAvailable(r.ok))
      .catch(() => setApkAvailable(false));
  }, []);

  const shortUrl = shareUrl.replace(/^https?:\/\//, "");
  const ready = apkAvailable === true;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the URL is already shown as text below */
    }
  };

  return (
    <div className="participate">
      <div className="participate-body">
        <header className="participate-header">
          <div className="participate-brand">
            <img src="/brand/ps_logotipo.png" alt="CICLOPE" />
          </div>
          <span className="participate-hashtag">#TodosSomosCICLOPE</span>
        </header>

        <section className="participate-hero">
          <p className="participate-eyebrow">AIM INSURTECH 2026 · STAND #4</p>
          <h1>Conviértete en recuperador.</h1>
          <p className="participate-lede">
            Un Beacon real está escondido en el venue. Descarga la app, síguele la señal con tu
            teléfono y ayúdanos a recuperarlo — sin registrarte, sin dar tus datos.
          </p>
        </section>

        <div className="participate-grid">
          <section className="participate-panel participate-download">
            <span className={"participate-status " + (ready ? "is-ready" : apkAvailable === false ? "is-pending" : "is-checking")}>
              <i />
              {ready ? "App disponible" : apkAvailable === false ? "Próximamente · consulta al Staff" : "Comprobando disponibilidad…"}
            </span>
            <h2>Descarga la app</h2>
            <a
              className={"participate-cta" + (!ready ? " participate-cta-disabled" : "")}
              href={APK_URL}
              aria-disabled={!ready}
            >
              Descargar para Android
            </a>
            {manifest && (
              <p className="participate-meta">
                Versión {manifest.versionName} · {formatBytes(manifest.sizeBytes)} · .apk
              </p>
            )}
            <p className="participate-hint">
              Android puede pedir permitir instalación desde el navegador la primera vez — ver
              ayuda abajo.
            </p>
          </section>

          <section className="participate-panel participate-qr">
            <h2>Escanea para abrir</h2>
            <div className="participate-qr-frame">
              <QrCode value={shareUrl} size={220} />
            </div>
            <p className="participate-shorturl">{shortUrl}</p>
            <button type="button" className="participate-copy" onClick={copyLink}>
              {copied ? "✓ Enlace copiado" : "Copiar enlace"}
            </button>
          </section>
        </div>

        <section className="participate-panel">
          <h2>Cómo participar</h2>
          <ol className="participate-steps">
            {STEPS.map((s, i) => (
              <li key={i}>
                <span className="participate-step-icon">{ICONS[s.icon]}</span>
                <span className="participate-step-num">{i + 1}</span>
                <p>{s.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <div className="participate-grid participate-grid-bottom">
          <section className="participate-panel participate-rules">
            <h2>Bases de participación</h2>
            <ul>
              {RULES.map((r) => <li key={r}>{r}</li>)}
            </ul>
          </section>

          <section className="participate-panel participate-faq">
            <h2>Preguntas frecuentes</h2>
            {FAQ.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </section>
        </div>

        <footer className="participate-footer">
          <span>CICLOPE</span>
          <span>STAND #4</span>
          <span>#TodosSomosCICLOPE</span>
        </footer>
      </div>
    </div>
  );
}
